import type { FormulationEntry } from "./compound";
import type { CostBreakdown } from "@/utils/formulation/computeCost";

// Distinct colors for comparison formulae — kept in sync between the radar
// plot, the scatter plot, and the Compare panel column accents.
export const CMP_COLORS = ["#7a59b8", "#c9802b"] as const;

// Source tag distinguishes user-owned variants from AI-generated alternatives.
export type CompareSource = "yours" | "similar" | "ai";

// Descriptor shape — top 1-2 substances + the always-visible item count.
// We split names from count so the row can pin the count and ellipsis the
// names independently.
export type CompareDescriptor = {
  names: string[];
  count: number;
};

// One pickable formula in the Compare popover, or one column in <ComparePanel>.
// Selected formulae carry the same shape (the popover toggles items in/out of
// the selection list) so the column rendering doesn't need a separate type.
export type CompareFormula = {
  id: string;
  name: string;
  // The popover renders compound-list descriptors (names + count); a few AI
  // fixtures may carry a hand-written tagline string for documentation.
  descriptor: CompareDescriptor | string;
  source: CompareSource;
  formulation: FormulationEntry[];
  /** Server-provided L²-squared loss for AI suggestions. Used in place of a
      client-computed loss when present (similar/yours rows leave it null and
      fall back to computed stats). */
  finalLossL2Sq?: number | null;
};

export type CompareCatalog = {
  yours: CompareFormula[];
  similar: CompareFormula[];
  ai: CompareFormula[];
};

// Per-formula stats used both in the popover (one row per pickable formula)
// and the ComparePanel header (per visible column). Mirrors VariantStats.
export type CompareStats = {
  loss: number | null;
  cost: CostBreakdown | null;
};

export type ComputeCompareStats = (
  formulation: FormulationEntry[]
) => CompareStats;

// Sensory overlay payload passed to RadarPlot/ScatterPlot. taste/smell are
// computed from the underlying formulation; x/y come from a deterministic
// scatter-of-scatter-points so the dot lands near the active flask.
export type CompareSensory = {
  id: string;
  name: string;
  descriptor: CompareDescriptor | string;
  color: string;
  taste: number[] | null;
  smell: number[] | null;
  x: number;
  y: number;
};
